import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Paper,
  TableContainer,
  Checkbox,
  Box,
  Button,
  Tooltip,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Edit as EditIcon, Undo as UndoIcon, Redo as RedoIcon } from '@mui/icons-material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useRevitData } from '../../providers/RevitDataContext';
import { borderRadius, getFieldBorderColor, getModernScrollbar, getModernTable, layoutGap } from '../../../theme/theme';
import SearchableDropdown from '../forms/SearchableDropdown';
import {
  DEFAULT_VIEWPORT_HEIGHT,
  HEADER_HEIGHT,
  ROW_HEIGHT,
  VIEWPORT_UPDATE_INTERVAL_MS,
  VIRTUALIZATION_ROW_THRESHOLD,
  buildGridTemplateColumns,
  clamp,
  describeColumnState,
  formatRowLabel,
  getCellRefKey,
  getDefaultColumnWidth,
  getEventClientX,
  getMinimumColumnWidth,
  getTableMinWidth,
  resolveVirtualRowRange,
  resolveColumnKey,
  slugifyColumnKey,
} from '../../lib/table';
import ColumnFilterMenu from './ColumnFilterMenu';
import { useTableHistory } from './hooks/useTableHistory';
import { OperationProgressPanel } from '../progress';
import {
  getWorkspaceFooterSurfaceSx,
  getWorkspaceTableSurfaceSx,
} from '../workflow/WorkflowWorkspacePrimitives';
import {
  useTableFilters,
  type TableColumnFilter,
  type TableFilterOperator,
  type TableInitialPreset,
  type TableSortDirection,
  type TableSortState,
} from './hooks/useTableFilters';
import type { QuickFillItem } from './types';
import type { RevitParameter } from '../../api/revit/types';
import { perf } from '../../lib/perfLogger';

// Destructuring defaults are evaluated per render; sharing one instance keeps
// memoized children (rows, header) stable when the caller omits the prop.
const EMPTY_HIGHLIGHTED_COLUMNS: (keyof QuickFillItem)[] = [];

// Static sx fragments hoisted so emotion's serialization cache (keyed by object
// identity) can skip re-hashing them on every cell render.
const inactiveEditableCellStaticSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  height: 32,
  px: 1,
  fontSize: '0.8125rem',
  borderRadius: `${borderRadius.sm}px`,
  border: '1px solid',
  cursor: 'text',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: 'text.primary',
};

const readOnlyCellStaticSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  minHeight: 32,
  px: 1,
  fontSize: '0.8125rem',
  borderRadius: `${borderRadius.sm}px`,
  border: '1px solid',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

const cellWrapperStaticSx = {
  py: layoutGap.dense,
  px: 1,
};

interface ActiveFindReplaceMatch {
  row: number;
  column: string;
}

const InactiveEditableCell = React.memo(function InactiveEditableCell({
  value,
  isEdited,
  isSelected,
  isFindReplaceMatch,
  isFailedCell,
  errorMessage,
  ariaLabel,
  onActivate,
  onSelectionMouseDown,
  onSelectionMouseEnter,
  onSelectionMouseUp,
  onSuppressClick,
}: {
  value: string;
  isEdited: boolean;
  isSelected: boolean;
  isFindReplaceMatch: boolean;
  isFailedCell: boolean;
  errorMessage?: string;
  ariaLabel?: string;
  onActivate: () => void;
  onSelectionMouseDown?: (event: React.MouseEvent) => void;
  onSelectionMouseEnter?: (event: React.MouseEvent) => void;
  onSelectionMouseUp?: () => void;
  onSuppressClick?: (event: React.MouseEvent) => void;
}) {
  const theme = useTheme();

  return (
    <Box
      onClickCapture={onSuppressClick}
      onClick={onActivate}
      onPointerDownCapture={onSelectionMouseDown}
      onPointerEnter={onSelectionMouseEnter}
      onPointerMove={onSelectionMouseEnter}
      onPointerUp={onSelectionMouseUp}
      onMouseDownCapture={onSelectionMouseDown}
      onMouseEnter={onSelectionMouseEnter}
      onMouseMove={onSelectionMouseEnter}
      onMouseOver={onSelectionMouseEnter}
      onMouseUp={onSelectionMouseUp}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === 'F2') {
          event.preventDefault();
          onActivate();
        }
      }}
      data-edited={isEdited ? 'true' : 'false'}
      sx={[
        inactiveEditableCellStaticSx,
        {
        borderColor: isFailedCell
          ? 'error.main'
          : errorMessage
          ? 'error.main'
          : isFindReplaceMatch
            ? 'warning.light'
          : isSelected
            ? 'primary.light'
            : isEdited
            ? 'primary.main'
            : alpha(theme.palette.divider, 0.6),
        backgroundColor: isFailedCell
          ? alpha(theme.palette.error.main, 0.14)
          : isFindReplaceMatch
            ? alpha(theme.palette.warning.main, 0.22)
          : isSelected
            ? alpha(theme.palette.primary.main, 0.24)
            : isEdited
            ? alpha(theme.palette.secondary.main, 0.2)
            : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.78 : 0.05),
        boxShadow: isFindReplaceMatch
          ? `inset 0 0 0 2px ${alpha(theme.palette.warning.light, 0.95)}, 0 0 0 1px ${alpha(theme.palette.warning.main, 0.35)}`
          : isSelected
          ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.85)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`
          : 'none',
        cursor: 'text',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: 'text.primary',
        fontWeight: isFindReplaceMatch || isSelected ? 600 : 400,
        '&:hover': {
          borderColor: isFindReplaceMatch ? 'warning.light' : isSelected ? 'primary.light' : 'primary.main',
        },
        },
      ]}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
    >
      {value || <Box component="span" sx={{ color: 'text.disabled' }}>Search…</Box>}
    </Box>
  );
});

const ReadOnlyCell = React.memo(function ReadOnlyCell({
  value,
  isReadOnlyColumn,
  isSelected,
  isFindReplaceMatch,
  isFailedCell,
}: {
  value: string;
  isReadOnlyColumn: boolean;
  isSelected: boolean;
  isFindReplaceMatch: boolean;
  isFailedCell: boolean;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={[
        readOnlyCellStaticSx,
        {
        borderColor: isFailedCell
          ? 'error.main'
          : isFindReplaceMatch
            ? 'warning.light'
          : isSelected
          ? 'primary.light'
          : isReadOnlyColumn
          ? alpha(theme.palette.warning.main, 0.32)
          : alpha(theme.palette.divider, 0.6),
        borderStyle: isReadOnlyColumn ? 'dashed' : 'solid',
        backgroundColor: isFindReplaceMatch
          ? alpha(theme.palette.warning.main, 0.18)
          : isReadOnlyColumn
          ? alpha(theme.palette.warning.main, 0.08)
          : isSelected
          ? alpha(theme.palette.primary.main, 0.18)
          : isFailedCell
          ? alpha(theme.palette.error.main, 0.12)
          : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.64 : 0.03),
        boxShadow: isFindReplaceMatch
          ? `inset 0 0 0 2px ${alpha(theme.palette.warning.light, 0.85)}`
          : isSelected ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.8)}` : 'none',
        color: isReadOnlyColumn ? 'warning.light' : 'text.primary',
        fontStyle: isReadOnlyColumn ? 'italic' : 'normal',
        opacity: isReadOnlyColumn ? 0.95 : 0.72,
        },
      ]}
    >
      {value}
    </Box>
  );
});

interface QuickFillTableRowProps {
  item: QuickFillItem;
  indexToUse: number;
  visibleColumns: (keyof QuickFillItem)[];
  highlightedColumns: (keyof QuickFillItem)[];
  gridTemplateColumns: string;
  selected: boolean;
  marked: boolean;
  activeFindReplaceMatch: ActiveFindReplaceMatch | null;
  editedCells: Record<string, boolean>;
  validationErrors: Record<string, string>;
  failedCellErrors: Record<string, string>;
  onSelectRow?: (index: number) => void;
  onCellClick?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseDown?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseEnter?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
  consumePendingCellClick?: () => boolean;
  isCellSelected: (rowIndex: number, columnLabel: string) => boolean;
  getOptionsForColumn: (column: keyof QuickFillItem, currentValue?: string) => { value: string; label: string }[];
  handleCellChange: (index: number, field: keyof QuickFillItem, value: string) => void;
  rowRefMap: React.MutableRefObject<Map<number, HTMLDivElement>>;
  cellRefMap: React.MutableRefObject<Map<string, HTMLDivElement>>;
  activeEditingRowIndex: number | null;
  activeEditingColumn: keyof QuickFillItem | null;
  setEditingCell: React.Dispatch<React.SetStateAction<EditingCell | null>>;
  editingCellRef: React.MutableRefObject<HTMLDivElement | null>;
}

const QuickFillTableRow = React.memo(function QuickFillTableRow({
  item,
  indexToUse,
  visibleColumns,
  highlightedColumns,
  gridTemplateColumns,
  selected,
  marked,
  activeFindReplaceMatch,
  editedCells,
  validationErrors,
  failedCellErrors,
  onSelectRow,
  onCellClick,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  consumePendingCellClick,
  isCellSelected,
  getOptionsForColumn,
  handleCellChange,
  rowRefMap,
  cellRefMap,
  activeEditingRowIndex,
  activeEditingColumn,
  setEditingCell,
  editingCellRef,
}: QuickFillTableRowProps) {
  const theme = useTheme();
  const fieldBorderColor = getFieldBorderColor(theme);

  return (
    <Box
      role="row"
      data-quick-fill-row="true"
      data-row-index={indexToUse}
      ref={(node: HTMLDivElement | null) => {
        if (node) {
          rowRefMap.current.set(indexToUse, node);
        } else {
          rowRefMap.current.delete(indexToUse);
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns,
        alignItems: 'stretch',
        minHeight: ROW_HEIGHT,
        ...(marked
          ? {
              '& > [role="cell"]:first-of-type': {
                boxShadow: `inset 3px 0 0 ${theme.palette.secondary.main}`,
              },
            }
          : {}),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      <Box
        role="cell"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: layoutGap.dense,
        }}
      >
        <Checkbox
          checked={selected}
          onChange={() => onSelectRow?.(indexToUse)}
          inputProps={{ 'aria-label': `Select row ${indexToUse + 1}` }}
          sx={{
            color: theme.palette.primary.main,
            '&.Mui-checked': { color: theme.palette.primary.main }
          }}
        />
      </Box>

      {visibleColumns.map((column) => {
        const cellId = `${indexToUse}-${column}`;
        const isEdited = editedCells[cellId];
        const errorMessage = validationErrors[cellId];
        const isNameColumn = column === 'Name';
        const isHighlightedColumn = highlightedColumns.includes(column);
        const rowReadonlyFlags = item.__readonlyFlags;
        const isReadOnlyCell = rowReadonlyFlags?.[String(column)] === true;
        const isNonEditable = isNameColumn || isReadOnlyCell;
        const isSelected = isCellSelected(indexToUse, String(column));
        const isActiveFindReplaceMatch =
          activeFindReplaceMatch?.row === indexToUse &&
          activeFindReplaceMatch.column === String(column);
        const failedError = failedCellErrors[cellId];
        const isFailedCell = !!failedError;
        const isEditingCell = activeEditingRowIndex === indexToUse && activeEditingColumn === column;
        const cellSelectionTarget = { rowIndex: indexToUse, columnLabel: String(column) };

        const handleClick = (e: React.MouseEvent) => {
          if (isNonEditable) return;
          onCellClick?.(cellSelectionTarget, e);
        };

        const handleClickCapture = (e: React.MouseEvent) => {
          if (isNonEditable || !consumePendingCellClick?.()) return;
          e.preventDefault();
          e.stopPropagation();
        };

        const handleMouseDown = (e: React.MouseEvent) => {
          if (isNonEditable || isEditingCell) return;
          onCellMouseDown?.(cellSelectionTarget, e);
        };

        const handleMouseEnter = (e: React.MouseEvent) => {
          if (isNonEditable || isEditingCell) return;
          onCellMouseEnter?.(cellSelectionTarget, e);
        };

        const editorBorderColor = isFailedCell || errorMessage ? theme.palette.error.main : fieldBorderColor;
        const cellEdgeBorder = isActiveFindReplaceMatch
          ? `1px solid ${alpha(theme.palette.warning.light, 0.7)}`
          : isHighlightedColumn ? `1px solid ${alpha(theme.palette.secondary.main, 0.12)}` : '1px solid transparent';

        return (
          <Box
            key={column}
            role="cell"
            ref={(node: HTMLDivElement | null) => {
              const cellRefKey = getCellRefKey(indexToUse, String(column));
              if (node) {
                cellRefMap.current.set(cellRefKey, node);
              } else {
                cellRefMap.current.delete(cellRefKey);
              }
            }}
            data-quick-fill-cell="true"
            data-row-index={indexToUse}
            data-column-label={String(column)}
            data-testid={`quick-fill-cell-${indexToUse}-${String(column)}`}
            data-selected={isSelected ? 'true' : 'false'}
            data-find-replace-active={isActiveFindReplaceMatch ? 'true' : 'false'}
            aria-current={isActiveFindReplaceMatch ? 'true' : undefined}
            aria-label={
              isActiveFindReplaceMatch
                ? `Active find and replace match, ${String(column)} for row ${indexToUse + 1}`
                : undefined
            }
            tabIndex={isActiveFindReplaceMatch ? 0 : undefined}
            onClickCapture={handleClickCapture}
            onClick={handleClick}
            onPointerDownCapture={handleMouseDown}
            onPointerEnter={handleMouseEnter}
            onPointerMove={handleMouseEnter}
            onPointerUp={onCellMouseUp}
            onMouseDownCapture={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseEnter}
            onMouseOver={handleMouseEnter}
            onMouseUp={onCellMouseUp}
            sx={[
              cellWrapperStaticSx,
              {
              cursor: !isNonEditable ? (isEditingCell ? 'text' : 'cell') : 'default',
              userSelect: !isNonEditable ? 'none' : 'auto',
              backgroundColor: isActiveFindReplaceMatch
                ? alpha(theme.palette.warning.main, 0.22)
                : isSelected
                ? alpha(theme.palette.primary.main, 0.24)
                : isFailedCell
                  ? alpha(theme.palette.error.main, 0.08)
                  : isHighlightedColumn
                    ? alpha(theme.palette.secondary.main, 0.08)
                    : 'transparent',
              boxShadow: isActiveFindReplaceMatch
                ? `inset 0 0 0 2px ${alpha(theme.palette.warning.light, 0.95)}, 0 0 0 2px ${alpha(theme.palette.warning.main, 0.28)}`
                : isSelected ? `inset 0 0 0 2px ${alpha(theme.palette.primary.light, 0.85)}` : 'none',
              borderTop: cellEdgeBorder,
              borderBottom: cellEdgeBorder,
              '&:hover': !isNonEditable ? {
                backgroundColor: isActiveFindReplaceMatch
                  ? alpha(theme.palette.warning.main, 0.28)
                  : isSelected
                  ? alpha(theme.palette.primary.main, 0.32)
                  : isHighlightedColumn
                    ? alpha(theme.palette.secondary.main, 0.12)
                    : alpha(theme.palette.primary.main, 0.05),
                transition: 'background-color 0.2s',
              } : {},
              '&:focus-visible': {
                transition: 'background-color 0.2s',
              },
              },
            ]}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: layoutGap.dense }}>
              {isNameColumn && marked ? (
                <Tooltip title="Navigation marker" placement="top">
                  <BookmarkIcon
                    fontSize="small"
                    sx={{ color: theme.palette.secondary.main, flexShrink: 0 }}
                  />
                </Tooltip>
              ) : null}
              {isNonEditable ? (
                <ReadOnlyCell
                  value={(item[column] as string) || ''}
                  isReadOnlyColumn={isReadOnlyCell}
                  isSelected={isSelected}
                  isFindReplaceMatch={isActiveFindReplaceMatch}
                  isFailedCell={isFailedCell}
                />
              ) : isEditingCell ? (
                <Box
                  ref={editingCellRef}
                  data-testid={`quick-fill-active-editor-${indexToUse}-${String(column)}`}
                  data-edited={isEdited ? 'true' : 'false'}
                  sx={{ flex: 1 }}
                >
                  <SearchableDropdown
                    value={(item[column] as string) || ''}
                    onChange={(value) => handleCellChange(indexToUse, column, value)}
                    options={getOptionsForColumn(column, item[column] as string)}
                    placeholder="Search…"
                    freeSolo
                    commitOnInput={false}
                    autoFocus
                    onCommit={() => setEditingCell(null)}
                    error={errorMessage}
                    ariaLabel={`${column} for row ${indexToUse + 1}`}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        height: 32,
                        fontSize: '0.8125rem',
                        backgroundColor: isFailedCell
                          ? alpha(theme.palette.error.main, 0.14)
                          : isActiveFindReplaceMatch
                            ? alpha(theme.palette.warning.main, 0.22)
                          : isSelected
                            ? alpha(theme.palette.primary.main, 0.24)
                          : isEdited
                            ? alpha(theme.palette.secondary.main, 0.2)
                            : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.78 : 0.05),
                        boxShadow: isActiveFindReplaceMatch
                          ? `inset 0 0 0 2px ${alpha(theme.palette.warning.light, 0.9)}`
                          : isSelected ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.85)}` : 'none',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: editorBorderColor,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: editorBorderColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: editorBorderColor,
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                </Box>
              ) : (
                <InactiveEditableCell
                  value={(item[column] as string) || ''}
                  isEdited={!!isEdited}
                  isSelected={isSelected}
                  isFindReplaceMatch={isActiveFindReplaceMatch}
                  isFailedCell={isFailedCell}
                  errorMessage={errorMessage}
                  ariaLabel={`${column} for row ${indexToUse + 1}`}
                  onActivate={() => setEditingCell({ rowIndex: indexToUse, column })}
                  onSelectionMouseDown={handleMouseDown}
                  onSelectionMouseEnter={handleMouseEnter}
                  onSelectionMouseUp={onCellMouseUp}
                  onSuppressClick={handleClickCapture}
                />
              )}
              {isEdited && !isNameColumn ? (
                <Tooltip title="This cell has unsaved changes.">
                  <EditIcon
                    fontSize="small"
                    sx={{ ml: layoutGap.dense, color: theme.palette.primary.main, alignSelf: 'center' }}
                  />
                </Tooltip>
              ) : null}
              {isFailedCell && !isNameColumn ? (
                <Tooltip title={failedError}>
                  <Typography
                    component="span"
                    sx={{ ml: layoutGap.dense, color: theme.palette.error.main, fontSize: '0.75rem', fontWeight: 700 }}
                  >
                    !
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}, (prev, next) =>
  prev.item === next.item &&
  prev.indexToUse === next.indexToUse &&
  prev.visibleColumns === next.visibleColumns &&
  prev.highlightedColumns === next.highlightedColumns &&
  prev.gridTemplateColumns === next.gridTemplateColumns &&
  prev.selected === next.selected &&
  prev.marked === next.marked &&
  prev.activeFindReplaceMatch === next.activeFindReplaceMatch &&
  prev.editedCells === next.editedCells &&
  prev.validationErrors === next.validationErrors &&
  prev.failedCellErrors === next.failedCellErrors &&
  prev.onSelectRow === next.onSelectRow &&
  prev.onCellClick === next.onCellClick &&
  prev.onCellMouseDown === next.onCellMouseDown &&
  prev.onCellMouseEnter === next.onCellMouseEnter &&
  prev.onCellMouseUp === next.onCellMouseUp &&
  prev.consumePendingCellClick === next.consumePendingCellClick &&
  prev.isCellSelected === next.isCellSelected &&
  prev.getOptionsForColumn === next.getOptionsForColumn &&
  prev.handleCellChange === next.handleCellChange &&
  prev.cellRefMap === next.cellRefMap &&
  prev.activeEditingRowIndex === next.activeEditingRowIndex &&
  prev.activeEditingColumn === next.activeEditingColumn
);

interface QuickFillProperty {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  checked: boolean;
  revitParameter?: string;
  isReadOnly?: boolean;
  dataType?: string;
  isInstance?: boolean;
}

interface TableFilterTourPreview {
  column: keyof QuickFillItem;
  open: boolean;
  interactive?: boolean;
  triggerRef?: React.RefObject<HTMLElement | null>;
  popoverRef?: React.RefObject<HTMLDivElement | null>;
}

interface EditingCell {
  rowIndex: number;
  column: keyof QuickFillItem;
}

interface FilterableTableHeaderColumnProps {
  column: keyof QuickFillItem;
  columnLabel: string;
  tooltipText: string;
  isHighlightedColumn: boolean;
  isResizeActive: boolean;
  sortDirection: TableSortDirection | null;
  activeFilter: TableColumnFilter | undefined;
  uniqueValues: string[];
  valueCounts: Record<string, number>;
  tourPreviewFilter: TableFilterTourPreview | null;
  tableHeaderSx: ReturnType<typeof getModernTable>['header'];
  setColumnValueFilter: (field: keyof QuickFillItem, selectedValues: string[] | null) => void;
  setColumnConditionFilter: (field: keyof QuickFillItem, operator: TableFilterOperator, value: string) => void;
  setColumnSort: (field: keyof QuickFillItem, direction: TableSortDirection) => void;
  clearColumnSort: () => void;
  clearColumnFilter: (field: keyof QuickFillItem) => void;
  startColumnResize: (event: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, column: keyof QuickFillItem) => void;
}

// Memoized per-column header cell: receives only referentially stable callbacks
// plus per-column data, so scroll-driven table re-renders skip it entirely.
const FilterableTableHeaderColumn = React.memo(function FilterableTableHeaderColumn({
  column,
  columnLabel,
  tooltipText,
  isHighlightedColumn,
  isResizeActive,
  sortDirection,
  activeFilter,
  uniqueValues,
  valueCounts,
  tourPreviewFilter,
  tableHeaderSx,
  setColumnValueFilter,
  setColumnConditionFilter,
  setColumnSort,
  clearColumnSort,
  clearColumnFilter,
  startColumnResize,
}: FilterableTableHeaderColumnProps) {
  const theme = useTheme();
  const resizeHandleId = `column-resize-handle-${slugifyColumnKey(String(column))}`;
  const isTourPreviewColumn = Boolean(tourPreviewFilter?.open && tourPreviewFilter.column === column);
  const isInteractiveTourColumn = Boolean(isTourPreviewColumn && tourPreviewFilter?.interactive);
  const isPreviewOnlyTourColumn = isTourPreviewColumn && !isInteractiveTourColumn;
  const columnSummary = isPreviewOnlyTourColumn
    ? 'Previewing filter flow'
    : describeColumnState(sortDirection, activeFilter);

  return (
    <Box
      role="columnheader"
      sx={{
        ...tableHeaderSx,
        py: 1,
        px: 1.5,
        pr: 2.5,
        position: 'relative',
        userSelect: isResizeActive ? 'none' : 'auto',
        backgroundColor: isHighlightedColumn
          ? alpha(theme.palette.secondary.main, 0.08)
          : tableHeaderSx.backgroundColor,
        boxShadow: isHighlightedColumn
          ? `inset 0 -2px 0 ${alpha(theme.palette.secondary.main, 0.45)}`
          : 'none',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: layoutGap.dense }}>
        <Tooltip title={tooltipText} arrow placement="top">
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: layoutGap.dense,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.64rem',
                color: isTourPreviewColumn || isHighlightedColumn ? theme.palette.secondary.light : theme.palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {columnLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.micro, flexShrink: 0 }}>
              <ColumnFilterMenu
                columnLabel={columnLabel}
                filter={activeFilter}
                uniqueValues={uniqueValues}
                valueCounts={valueCounts}
                onApplyValues={(selectedValues) => setColumnValueFilter(column, selectedValues)}
                onApplyCondition={(operator, value) => setColumnConditionFilter(column, operator, value)}
                sortDirection={sortDirection}
                onSortAscending={() => setColumnSort(column, 'asc')}
                onSortDescending={() => setColumnSort(column, 'desc')}
                onClearSort={clearColumnSort}
                onClear={() => clearColumnFilter(column)}
                triggerRef={isTourPreviewColumn ? tourPreviewFilter?.triggerRef : undefined}
                popoverRef={isTourPreviewColumn ? tourPreviewFilter?.popoverRef : undefined}
                previewOpen={isTourPreviewColumn}
                previewMode={isPreviewOnlyTourColumn}
                emphasizeTrigger={isTourPreviewColumn}
              />
            </Box>
          </Box>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            minHeight: 12,
            fontSize: '0.56rem',
            lineHeight: 1.1,
            color: columnSummary !== 'All values' ? theme.palette.secondary.light : alpha(theme.palette.text.secondary, 0.52),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: isTourPreviewColumn ? 700 : 500,
          }}
        >
          {columnSummary}
        </Typography>
      </Box>
      <Box
        role="separator"
        aria-label={`Resize ${columnLabel} column`}
        aria-orientation="vertical"
        data-testid={resizeHandleId}
        onPointerDown={(event) => startColumnResize(event, column)}
        onMouseDown={(event) => startColumnResize(event, column)}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 10,
          height: '100%',
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          '&::before': {
            content: '""',
            width: 2,
            height: '55%',
            borderRadius: `${borderRadius.full}px`,
            backgroundColor: isResizeActive
              ? theme.palette.secondary.main
              : alpha(theme.palette.common.white, 0.18),
            transition: 'background-color 0.2s ease',
          },
          '&:hover::before': {
            backgroundColor: theme.palette.secondary.light,
          },
        }}
      />
    </Box>
  );
});

interface FilterableTableHeaderProps {
  gridTemplateColumns: string;
  tableHeaderSx: ReturnType<typeof getModernTable>['header'];
  visibleColumns: (keyof QuickFillItem)[];
  highlightedColumns: (keyof QuickFillItem)[];
  propertyByColumn: Partial<Record<keyof QuickFillItem, QuickFillProperty>>;
  revitParameters: RevitParameter[];
  sortState: TableSortState;
  columnFilters: Partial<Record<keyof QuickFillItem, TableColumnFilter>>;
  tourPreviewFilter: TableFilterTourPreview | null;
  activeResizeColumn: keyof QuickFillItem | null;
  allVisibleRowsSelected: boolean;
  selectedVisibleRowCount: number;
  visibleRowIndices: number[];
  onSelectAllRows?: (rowIndices: number[]) => void;
  getColumnDisplayLabel: (column: keyof QuickFillItem) => string;
  getUniqueValuesForColumn: (field: keyof QuickFillItem) => string[];
  getValueCountsForColumn: (field: keyof QuickFillItem) => Record<string, number>;
  setColumnValueFilter: (field: keyof QuickFillItem, selectedValues: string[] | null) => void;
  setColumnConditionFilter: (field: keyof QuickFillItem, operator: TableFilterOperator, value: string) => void;
  setColumnSort: (field: keyof QuickFillItem, direction: TableSortDirection) => void;
  clearColumnSort: () => void;
  clearColumnFilter: (field: keyof QuickFillItem) => void;
  startColumnResize: (event: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, column: keyof QuickFillItem) => void;
}

// Sticky header extracted so virtualRange (scroll) updates re-render only the
// virtualized rows; every prop passed here is memoized or useCallback-stable.
const FilterableTableHeader = React.memo(function FilterableTableHeader({
  gridTemplateColumns,
  tableHeaderSx,
  visibleColumns,
  highlightedColumns,
  propertyByColumn,
  revitParameters,
  sortState,
  columnFilters,
  tourPreviewFilter,
  activeResizeColumn,
  allVisibleRowsSelected,
  selectedVisibleRowCount,
  visibleRowIndices,
  onSelectAllRows,
  getColumnDisplayLabel,
  getUniqueValuesForColumn,
  getValueCountsForColumn,
  setColumnValueFilter,
  setColumnConditionFilter,
  setColumnSort,
  clearColumnSort,
  clearColumnFilter,
  startColumnResize,
}: FilterableTableHeaderProps) {
  const theme = useTheme();

  return (
    <Box
      role="rowgroup"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
      }}
    >
      <Box
        role="row"
        sx={{
          display: 'grid',
          gridTemplateColumns,
          minHeight: HEADER_HEIGHT,
          alignItems: 'stretch',
        }}
      >
        <Box
          role="columnheader"
          sx={{
            ...tableHeaderSx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: layoutGap.dense,
          }}
        >
          <Checkbox
            checked={allVisibleRowsSelected}
            indeterminate={selectedVisibleRowCount > 0 && !allVisibleRowsSelected}
            onChange={() => onSelectAllRows?.(visibleRowIndices)}
            inputProps={{ 'aria-label': 'Select all rows' }}
            sx={{
              color: theme.palette.primary.main,
              '&.Mui-checked': { color: theme.palette.primary.main }
            }}
          />
        </Box>

        {visibleColumns.map((column) => {
          const columnProperty = propertyByColumn[column];
          const mappedParameterName = columnProperty?.revitParameter;
          const revitParam = mappedParameterName && Array.isArray(revitParameters)
            ? revitParameters.find(p => p && p.name === mappedParameterName)
            : undefined;
          const columnLabel = getColumnDisplayLabel(column);
          const parameterLabel = columnProperty?.label || 'Not mapped';
          const tooltipText = revitParam?.description
            ? `${columnLabel}. Mapped attribute: ${parameterLabel}. ${revitParam.description}`
            : `${columnLabel}. Mapped attribute: ${parameterLabel}.`;

          return (
            <FilterableTableHeaderColumn
              key={column}
              column={column}
              columnLabel={columnLabel}
              tooltipText={tooltipText}
              isHighlightedColumn={highlightedColumns.includes(column)}
              isResizeActive={activeResizeColumn === column}
              sortDirection={sortState.column === column ? sortState.direction : null}
              activeFilter={columnFilters[column]}
              uniqueValues={getUniqueValuesForColumn(column)}
              valueCounts={getValueCountsForColumn(column)}
              tourPreviewFilter={tourPreviewFilter}
              tableHeaderSx={tableHeaderSx}
              setColumnValueFilter={setColumnValueFilter}
              setColumnConditionFilter={setColumnConditionFilter}
              setColumnSort={setColumnSort}
              clearColumnSort={clearColumnSort}
              clearColumnFilter={clearColumnFilter}
              startColumnResize={startColumnResize}
            />
          );
        })}
      </Box>
    </Box>
  );
});

interface FilterableTableProps {
  data: QuickFillItem[];
  onRowChange: (index: number, field: keyof QuickFillItem, value: string) => void;
  selectedProperties?: QuickFillProperty[];
  nameColumnLabel?: string;
  highlightedColumns?: (keyof QuickFillItem)[];
  externalEditedCells?: Record<string, boolean>;
  selectedRows?: number[];
  onSelectRow?: (index: number) => void;
  onSelectAllRows?: (rowIndices: number[]) => void;
  onCellClick?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseDown?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseEnter?: (cell: { rowIndex: number; columnLabel: string }, event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
  consumePendingCellClick?: () => boolean;
  isCellSelected?: (rowIndex: number, columnLabel: string) => boolean;
  failedCellErrors?: Record<string, string>;
  isLoading?: boolean;
  loadingText?: string;
  markedRowIndices?: number[];
  navigationRequest?: { rowIndex: number; requestId: number; columnLabel?: string } | null;
  activeFindReplaceMatch?: ActiveFindReplaceMatch | null;
  onVisibleRowIndicesChange?: (rowIndices: number[]) => void;
  onViewportRangeChange?: (range: { startPosition: number | null; endPosition: number | null; totalFilteredRows: number }) => void;
  tableViewportRef?: React.Ref<HTMLDivElement>;
  tourPreviewFilter?: TableFilterTourPreview | null;
  initialPreset?: TableInitialPreset | null;
}

interface ActiveColumnResize {
  column: keyof QuickFillItem;
  startX: number;
  startWidth: number;
}

const FilterableTable: React.FC<FilterableTableProps> = ({
  data,
  onRowChange,
  selectedProperties,
  nameColumnLabel = 'Name',
  highlightedColumns = EMPTY_HIGHLIGHTED_COLUMNS,
  externalEditedCells = {},
  selectedRows = [],
  onSelectRow,
  onSelectAllRows,
  onCellClick,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  consumePendingCellClick,
  isCellSelected = () => false,
  failedCellErrors = {},
  isLoading = false,
  loadingText,
  markedRowIndices = [],
  navigationRequest = null,
  activeFindReplaceMatch = null,
  onVisibleRowIndicesChange,
  onViewportRangeChange,
  tableViewportRef,
  tourPreviewFilter = null,
  initialPreset = null,
}) => {
  const renderStartedAt = performance.now();
  const { parameters: revitParameters } = useRevitData();
  const theme = useTheme();
  const isCompactTable = useMediaQuery(theme.breakpoints.down('md'));
  const tableStyles = useMemo(() => getModernTable(theme), [theme]);
  const scrollbarStyles = useMemo(() => getModernScrollbar(theme), [theme]);
  const tableSurfaceSx = useMemo(() => getWorkspaceTableSurfaceSx(theme), [theme]);
  const footerSurfaceSx = useMemo(() => getWorkspaceFooterSurfaceSx(theme), [theme]);
  const rowRefs = useRef(new Map<number, HTMLDivElement>());
  const cellRefs = useRef(new Map<string, HTMLDivElement>());
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const editingCellRef = useRef<HTMLDivElement | null>(null);
  const rowHeightRef = useRef<number>(ROW_HEIGHT);
  const lastViewportRangeRef = useRef<string>('');
  const lastViewportUpdateTimestampRef = useRef(0);
  const lastRenderDiagnosticRef = useRef<{
    rows: number;
    filteredRows: number;
    visibleColumns: number;
    isLoading: boolean;
  } | null>(null);
  const pendingViewportRangeRef = useRef<{ startPosition: number; endPosition: number; totalFilteredRows: number } | null>(null);
  const activeResizeRef = useRef<ActiveColumnResize | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const resizeAnimationFrameRef = useRef<number | null>(null);
  const resizePendingWidthRef = useRef<{ column: keyof QuickFillItem; width: number } | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [virtualRange, setVirtualRange] = useState({ start: 0, end: 0 });
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [activeResizeColumn, setActiveResizeColumn] = useState<keyof QuickFillItem | null>(null);

  // Ensure data is always an array
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Get all possible column keys from the first item in data, or define them explicitly
  const allColumns = useMemo(() => {
    if (safeData.length > 0) {
      return Object.keys(safeData[0]) as (keyof QuickFillItem)[];
    }
    return ['Name', 'SKEY', 'OBJECT-TYPE', 'End1', 'End2', 'End3', 'End4', 'PIPING-SPEC',
            'SYSTEM-GROUPING', 'MATERIAL', 'DESIGN-STD', 'PRESSURE-CLASS',
            'INSULATION-SPEC', 'INSULATION-THICKNESS', 'TRACING-SPEC', 'ITEM-DESCRIPTION'];
  }, [safeData]);

  // Determine which columns to show based on selected properties
  const visibleColumns = useMemo(() => {
    if (Array.isArray(selectedProperties)) {
      const columns: (keyof QuickFillItem)[] = ['Name'];
      selectedProperties.forEach(property => {
        if (property.checked) {
          const key = resolveColumnKey(property.label) as keyof QuickFillItem;
          if (key && !columns.includes(key)) {
            columns.push(key);
          }
        }
      });
      return columns;
    } else {
      return allColumns;
    }
  }, [selectedProperties, allColumns]);

  useEffect(() => {
    setColumnWidths((previous) => {
      let changed = false;
      const next = { ...previous };

      visibleColumns.forEach((column) => {
        if (next[column] === undefined) {
          next[column] = getDefaultColumnWidth(column);
          changed = true;
        }
      });

      return changed ? next : previous;
    });
  }, [visibleColumns]);

  const propertyByColumn = useMemo(() => {
    const map: Partial<Record<keyof QuickFillItem, QuickFillProperty>> = {};
    (selectedProperties || []).forEach((property) => {
      if (!property) return;
      const key = resolveColumnKey(property.label) as keyof QuickFillItem;
      if (key && !map[key]) {
        map[key] = property;
      }
    });
    return map;
  }, [selectedProperties]);

  // Extracted hooks
  const {
    editedCells,
    validationErrors,
    historyIndex,
    historyLength,
    changeSummary,
    handleCellChange,
    handleUndo,
    handleRedo,
  } = useTableHistory(safeData as Parameters<typeof useTableHistory>[0], onRowChange, selectedProperties);

  const mergedEditedCells = useMemo(
    () => ({ ...editedCells, ...externalEditedCells }),
    [editedCells, externalEditedCells]
  );

  const {
    columnFilters,
    getUniqueValuesForColumn,
    getValueCountsForColumn,
    setColumnValueFilter,
    setColumnConditionFilter,
    setColumnSort,
    clearColumnSort,
    clearColumnFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
    filteredData,
    sortState,
  } = useTableFilters(safeData as Parameters<typeof useTableFilters>[0], allColumns, initialPreset);

  const visibleRowIndices = useMemo(() => {
    const tVisibleRows = performance.now();
    // Build identity Map once: O(n) instead of O(n²) indexOf per row
    const itemToIndex = new Map<QuickFillItem, number>();
    safeData.forEach((item, idx) => itemToIndex.set(item, idx));
    const indices = filteredData.map((item, filteredIndex) => {
      return itemToIndex.get(item) ?? filteredIndex;
    });
    perf.slow('QF-Table', 'build visible row index map', tVisibleRows, 40, {
      totalRows: safeData.length,
      filteredRows: filteredData.length,
      visibleRows: indices.length,
    });
    return indices;
  }, [filteredData, safeData]);

  const selectedVisibleRowCount = useMemo(() => {
    const selectedSet = new Set(selectedRows);
    return visibleRowIndices.filter((idx) => selectedSet.has(idx)).length;
  }, [visibleRowIndices, selectedRows]);

  const allVisibleRowsSelected = useMemo(() => {
    return visibleRowIndices.length > 0 && selectedVisibleRowCount === visibleRowIndices.length;
  }, [visibleRowIndices, selectedVisibleRowCount]);

  // Set for O(1) row selection check in render loop
  const selectedRowSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const markedRowSet = useMemo(() => new Set(markedRowIndices), [markedRowIndices]);

  useEffect(() => {
    const currentDiagnostic = {
      rows: safeData.length,
      filteredRows: filteredData.length,
      visibleColumns: visibleColumns.length,
      isLoading,
    };
    const previousDiagnostic = lastRenderDiagnosticRef.current;
    const structureChanged =
      !previousDiagnostic ||
      previousDiagnostic.rows !== currentDiagnostic.rows ||
      previousDiagnostic.filteredRows !== currentDiagnostic.filteredRows ||
      previousDiagnostic.visibleColumns !== currentDiagnostic.visibleColumns ||
      previousDiagnostic.isLoading !== currentDiagnostic.isLoading;
    lastRenderDiagnosticRef.current = currentDiagnostic;

    perf.slow('QF-Table', 'render and commit', renderStartedAt, structureChanged ? 75 : 250, {
      rows: safeData.length,
      filteredRows: filteredData.length,
      visibleColumns: visibleColumns.length,
      virtualStart: virtualRange.start,
      virtualEnd: virtualRange.end,
      isLoading,
    });
  });

  const clearResizePresentation = useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const flushPendingResizeWidth = useCallback(() => {
    const pending = resizePendingWidthRef.current;
    if (!pending) {
      return;
    }

    resizePendingWidthRef.current = null;
    setColumnWidths((previous) =>
      previous[pending.column] === pending.width
        ? previous
        : {
            ...previous,
            [pending.column]: pending.width,
          }
    );
  }, []);

  const clearResizeAnimationFrame = useCallback(() => {
    if (resizeAnimationFrameRef.current != null) {
      window.cancelAnimationFrame(resizeAnimationFrameRef.current);
      resizeAnimationFrameRef.current = null;
    }
  }, []);

  const scheduleResizeWidth = useCallback((column: keyof QuickFillItem, width: number) => {
    resizePendingWidthRef.current = { column, width };

    if (resizeAnimationFrameRef.current != null) {
      return;
    }

    resizeAnimationFrameRef.current = window.requestAnimationFrame(() => {
      resizeAnimationFrameRef.current = null;
      flushPendingResizeWidth();
    });
  }, [flushPendingResizeWidth]);

  const stopColumnResize = useCallback(() => {
    resizeCleanupRef.current?.();
    resizeCleanupRef.current = null;
    activeResizeRef.current = null;
    setActiveResizeColumn(null);
    clearResizeAnimationFrame();
    flushPendingResizeWidth();
    clearResizePresentation();
  }, [clearResizeAnimationFrame, clearResizePresentation, flushPendingResizeWidth]);

  const startColumnResize = useCallback((event: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, column: keyof QuickFillItem) => {
    event.preventDefault();
    event.stopPropagation();

    resizeCleanupRef.current?.();
    activeResizeRef.current = {
      column,
      startX: getEventClientX(event),
      startWidth: columnWidths[column] ?? getDefaultColumnWidth(column),
    };
    setActiveResizeColumn(column);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const updateResizeWidth = (clientX: number) => {
      const activeResize = activeResizeRef.current;
      if (!activeResize) {
        return;
      }

      const nextWidth = Math.max(
        getMinimumColumnWidth(activeResize.column),
        Math.round(activeResize.startWidth + (clientX - activeResize.startX))
      );

      scheduleResizeWidth(activeResize.column, nextWidth);
    };

    const handleWindowPointerMove = (event: PointerEvent) => {
      event.preventDefault();
      updateResizeWidth(getEventClientX(event));
    };

    const handleWindowMouseMove = (event: MouseEvent) => {
      event.preventDefault();
      updateResizeWidth(getEventClientX(event));
    };

    const handleResizeEnd = () => {
      stopColumnResize();
    };

    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleResizeEnd);
    window.addEventListener('pointercancel', handleResizeEnd);
    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleResizeEnd);

    resizeCleanupRef.current = () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleResizeEnd);
      window.removeEventListener('pointercancel', handleResizeEnd);
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [columnWidths, scheduleResizeWidth, stopColumnResize]);

  useEffect(() => () => {
    resizeCleanupRef.current?.();
    resizeCleanupRef.current = null;
    activeResizeRef.current = null;
    clearResizeAnimationFrame();
    resizePendingWidthRef.current = null;
    clearResizePresentation();
  }, [clearResizeAnimationFrame, clearResizePresentation]);

  useEffect(() => {
    onVisibleRowIndicesChange?.(visibleRowIndices);
  }, [onVisibleRowIndicesChange, visibleRowIndices]);

  useEffect(() => {
    if (!editingCell) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      // MUI renders the autocomplete popper in a portal outside editingCellRef, so the ref-contains check alone can't detect clicks inside the dropdown.
      const insideAutocompletePopper =
        target instanceof Element && Boolean(target.closest('.MuiAutocomplete-popper'));

      if (
        editingCellRef.current &&
        !editingCellRef.current.contains(target as Node) &&
        !insideAutocompletePopper
      ) {
        setEditingCell(null);
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [editingCell]);

  const flushViewportRange = useCallback(() => {
    const pendingRange = pendingViewportRangeRef.current;
    if (!pendingRange) {
      return;
    }

    lastViewportUpdateTimestampRef.current = performance.now();
    pendingViewportRangeRef.current = null;
    onViewportRangeChange?.(pendingRange);
  }, [onViewportRangeChange]);

  const syncViewportState = useCallback(() => {
    const container = tableContainerRef.current;
    if (filteredData.length === 0) {
      onViewportRangeChange?.({ startPosition: null, endPosition: null, totalFilteredRows: 0 });
      setVirtualRange((previous) => (previous.start === 0 && previous.end === 0 ? previous : { start: 0, end: 0 }));
      return;
    }

    const maxStart = Math.max(filteredData.length - 1, 0);
    const viewportHeight = container?.clientHeight || DEFAULT_VIEWPORT_HEIGHT;
    const scrollTop = container?.scrollTop || 0;
    const viewportStart = clamp(
      Math.floor(scrollTop / Math.max(rowHeightRef.current, 1)),
      0,
      maxStart
    );
    const visibleCount = Math.max(
      1,
      Math.ceil(viewportHeight / Math.max(rowHeightRef.current, 1))
    );
    const nextRange = {
      startPosition: viewportStart,
      endPosition: clamp(viewportStart + visibleCount - 1, 0, maxStart),
      totalFilteredRows: filteredData.length,
    };
    const nextRangeKey = `${nextRange.startPosition}:${nextRange.endPosition}:${nextRange.totalFilteredRows}`;
    setVirtualRange((previous) => {
      const nextVirtualRange = resolveVirtualRowRange({
        rowCount: filteredData.length,
        viewportStart,
        visibleCount,
        previousRange: previous,
      });

      if (previous.start === nextVirtualRange.start && previous.end === nextVirtualRange.end) {
        return previous;
      }

      return nextVirtualRange;
    });
    if (nextRangeKey === lastViewportRangeRef.current) {
      return;
    }
    lastViewportRangeRef.current = nextRangeKey;

    const elapsed = performance.now() - lastViewportUpdateTimestampRef.current;
    if (elapsed >= VIEWPORT_UPDATE_INTERVAL_MS) {
      lastViewportUpdateTimestampRef.current = performance.now();
      onViewportRangeChange?.(nextRange);
      return;
    }

    pendingViewportRangeRef.current = nextRange;
  }, [filteredData, onViewportRangeChange]);

  useEffect(() => {
    syncViewportState();
  }, [syncViewportState]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) {
      return;
    }

    let frameId = 0;
    let timeoutId: number | null = null;
    const scheduleViewportReport = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(syncViewportState);

      if (timeoutId != null || !pendingViewportRangeRef.current) {
        return;
      }

      const elapsed = performance.now() - lastViewportUpdateTimestampRef.current;
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        flushViewportRange();
      }, Math.max(0, VIEWPORT_UPDATE_INTERVAL_MS - elapsed));
    };

    container.addEventListener('scroll', scheduleViewportReport, { passive: true });
    window.addEventListener('resize', scheduleViewportReport, { passive: true });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
      pendingViewportRangeRef.current = null;
      container.removeEventListener('scroll', scheduleViewportReport);
      window.removeEventListener('resize', scheduleViewportReport);
    };
  }, [flushViewportRange, syncViewportState]);

  useEffect(() => {
    if (!navigationRequest) {
      return;
    }

    const container = tableContainerRef.current;
    const targetVisibleIndex = visibleRowIndices.indexOf(navigationRequest.rowIndex);
    if (targetVisibleIndex < 0 || !container) {
      return;
    }

    if (filteredData.length > VIRTUALIZATION_ROW_THRESHOLD) {
      container.scrollTop = targetVisibleIndex * Math.max(rowHeightRef.current, 1);
      return;
    }

    const timerId = window.setTimeout(() => {
      rowRefs.current.get(navigationRequest.rowIndex)?.scrollIntoView({ block: 'center', inline: 'nearest' });
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [filteredData.length, navigationRequest, visibleRowIndices]);

  useEffect(() => {
    if (!activeFindReplaceMatch) {
      return;
    }

    const container = tableContainerRef.current;
    const targetVisibleIndex = visibleRowIndices.indexOf(activeFindReplaceMatch.row);
    if (targetVisibleIndex < 0 || !container) {
      return;
    }

    if (filteredData.length > VIRTUALIZATION_ROW_THRESHOLD) {
      container.scrollTop = targetVisibleIndex * Math.max(rowHeightRef.current, 1);
      syncViewportState();
    }

    const scrollActiveMatchIntoView = () => {
      const cellRefKey = getCellRefKey(activeFindReplaceMatch.row, activeFindReplaceMatch.column);
      const target =
        cellRefs.current.get(cellRefKey) ??
        rowRefs.current.get(activeFindReplaceMatch.row);

      target?.scrollIntoView({ block: 'center', inline: 'center' });
    };

    const frameId = window.requestAnimationFrame(scrollActiveMatchIntoView);
    const timerId = window.setTimeout(scrollActiveMatchIntoView, 0);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timerId);
    };
  }, [
    activeFindReplaceMatch,
    filteredData.length,
    syncViewportState,
    virtualRange.end,
    virtualRange.start,
    visibleRowIndices,
  ]);

  const getColumnDisplayLabel = useCallback((column: keyof QuickFillItem) => {
    if (column === 'Name') {
      return nameColumnLabel;
    }

    return propertyByColumn[column]?.revitParameter || String(column);
  }, [nameColumnLabel, propertyByColumn]);

  // Get options for a specific column from selectedProperties
  const getOptionsForColumn = useCallback((column: keyof QuickFillItem, currentValue?: string): { value: string; label: string }[] => {
    const property = Array.isArray(selectedProperties)
      ? selectedProperties.find(prop => prop && (prop.label === column || prop.label === String(column)))
      : undefined;
    let options: { value: string; label: string }[] = [];

    if (property && Array.isArray(property.options) && property.options.length > 0) {
      options = property.options.filter(opt => opt && typeof opt === 'object' && 'value' in opt && 'label' in opt);
    }

    if (currentValue && currentValue !== '' && !options.find(opt => opt.value === currentValue)) {
      options.push({ value: currentValue, label: currentValue });
    }

    if (!options.find(opt => opt.value === '')) {
      options.unshift({ value: '', label: '(Select…)' });
    }

    return options;
  }, [selectedProperties]);

  const tableMinWidth = useMemo(
    () => getTableMinWidth(visibleColumns, columnWidths),
    [columnWidths, visibleColumns]
  );
  const gridTemplateColumns = useMemo(
    () => buildGridTemplateColumns(visibleColumns, columnWidths),
    [columnWidths, visibleColumns]
  );
  const shouldVirtualizeRows = filteredData.length > VIRTUALIZATION_ROW_THRESHOLD;
  const visibleRowStart = shouldVirtualizeRows ? virtualRange.start : 0;
  const visibleRowEnd = shouldVirtualizeRows ? virtualRange.end + 1 : filteredData.length;
  const visibleRows = filteredData.slice(visibleRowStart, visibleRowEnd);
  const pendingChangeCount = Object.keys(changeSummary).length;
  const emptyTableMessage = safeData.length === 0
    ? 'No Quick Fill rows are available yet.'
    : 'No rows match the current filters.';
  const visibleRowStatus = hasActiveFilters
    ? `${filteredData.length} of ${formatRowLabel(safeData.length)} visible`
    : `${formatRowLabel(safeData.length)} visible`;
  const selectedVisibleRowStatus = `${formatRowLabel(selectedVisibleRowCount)} selected in view`;
  const activeFindReplaceStatus = activeFindReplaceMatch
    ? `Active find and replace match: row ${activeFindReplaceMatch.row + 1}, ${activeFindReplaceMatch.column}`
    : null;

  return (
    <Paper
      elevation={0}
      sx={[
        tableSurfaceSx,
        {
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }]}
    >
      {activeFindReplaceStatus ? (
        <Box
          data-testid="quick-fill-find-replace-status"
          role="status"
          aria-live="polite"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {activeFindReplaceStatus}
        </Box>
      ) : null}
      {/* Loading overlay — non-blocking, shown on top of table content */}
      {isLoading && (
        <Box
          data-testid="table-loading-overlay"
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.72 : 0.82),
            backdropFilter: 'blur(3px)',
            zIndex: 20,
            p: 2,
          }}
        >
          <OperationProgressPanel
            compact
            model={{
              title: 'Loading table',
              message: loadingText || 'Preparing rows',
              detail: 'The table will refresh as soon as the current data is ready.',
              status: 'running',
              percent: null,
              phase: 'Preparing rows',
              locked: false,
            }}
            sx={{ maxWidth: 520 }}
            testId="table-loading-progress"
          />
        </Box>
      )}
      <TableContainer
        ref={tableContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: 'none',
          overflow: 'auto',
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.36 : 0.12),
          ...scrollbarStyles
        }}
      >
        <Box
          ref={tableViewportRef}
          data-testid="quick-fill-table-viewport"
          sx={{
            width: '100%',
            minWidth: 0,
          }}
        >
        <Box
          role="table"
          data-testid="quick-fill-table"
          aria-label="Quick Fill editable table"
          data-grid-template-columns={gridTemplateColumns}
          sx={{ minWidth: tableMinWidth }}
        >
          <FilterableTableHeader
            gridTemplateColumns={gridTemplateColumns}
            tableHeaderSx={tableStyles.header}
            visibleColumns={visibleColumns}
            highlightedColumns={highlightedColumns}
            propertyByColumn={propertyByColumn}
            revitParameters={revitParameters}
            sortState={sortState}
            columnFilters={columnFilters}
            tourPreviewFilter={tourPreviewFilter}
            activeResizeColumn={activeResizeColumn}
            allVisibleRowsSelected={allVisibleRowsSelected}
            selectedVisibleRowCount={selectedVisibleRowCount}
            visibleRowIndices={visibleRowIndices}
            onSelectAllRows={onSelectAllRows}
            getColumnDisplayLabel={getColumnDisplayLabel}
            getUniqueValuesForColumn={getUniqueValuesForColumn}
            getValueCountsForColumn={getValueCountsForColumn}
            setColumnValueFilter={setColumnValueFilter}
            setColumnConditionFilter={setColumnConditionFilter}
            setColumnSort={setColumnSort}
            clearColumnSort={clearColumnSort}
            clearColumnFilter={clearColumnFilter}
            startColumnResize={startColumnResize}
          />

          <Box
            role="rowgroup"
            sx={{
              position: 'relative',
              height: filteredData.length === 0 ? 'auto' : filteredData.length * ROW_HEIGHT,
              minHeight: filteredData.length === 0 ? 180 : undefined,
            }}
          >
            {filteredData.length === 0 ? (
              <Box
                role="row"
                sx={{
                  display: 'grid',
                  gridTemplateColumns,
                  minHeight: 180,
                  alignItems: 'center',
                }}
              >
                <Box role="cell" sx={{ gridColumn: `1 / span ${visibleColumns.length + 1}`, py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyTableMessage}
                  </Typography>
                  {hasActiveFilters && (
                    <Button
                      onClick={resetFilters}
                      variant="text"
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Box>
              </Box>
            ) : null}
            {visibleRows.map((item, visibleIndex) => {
              const filteredIndex = visibleRowStart + visibleIndex;
              const indexToUse = visibleRowIndices[filteredIndex] ?? filteredIndex;

              return (
                <Box
                  key={indexToUse}
                  sx={{
                    position: 'absolute',
                    top: filteredIndex * ROW_HEIGHT,
                    left: 0,
                    right: 0,
                  }}
                >
                  <QuickFillTableRow
                    item={item}
                    indexToUse={indexToUse}
                    visibleColumns={visibleColumns}
                    highlightedColumns={highlightedColumns}
                    activeFindReplaceMatch={activeFindReplaceMatch}
                    gridTemplateColumns={gridTemplateColumns}
                    selected={selectedRowSet.has(indexToUse)}
                    marked={markedRowSet.has(indexToUse)}
                    editedCells={mergedEditedCells}
                    validationErrors={validationErrors}
                    failedCellErrors={failedCellErrors}
                    onSelectRow={onSelectRow}
                    onCellClick={onCellClick}
                    onCellMouseDown={onCellMouseDown}
                    onCellMouseEnter={onCellMouseEnter}
                    onCellMouseUp={onCellMouseUp}
                    consumePendingCellClick={consumePendingCellClick}
                    isCellSelected={isCellSelected}
                    getOptionsForColumn={getOptionsForColumn}
                    handleCellChange={handleCellChange}
                    rowRefMap={rowRefs}
                    cellRefMap={cellRefs}
                    activeEditingRowIndex={editingCell?.rowIndex ?? null}
                    activeEditingColumn={editingCell?.column ?? null}
                    setEditingCell={setEditingCell}
                    editingCellRef={editingCellRef}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        </Box>
      </TableContainer>

      {/* Compact utility strip */}
      <Box
        sx={[
          {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: layoutGap.row,
          px: 1,
          py: 0.375,
          minHeight: 32,
          flexShrink: 0,
        },
          footerSurfaceSx,
        ]}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.micro }}>
            <IconButton
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              aria-label="Undo"
              sx={{
                width: 24,
                height: 24,
                color: historyIndex > 0 ? theme.palette.primary.main : theme.palette.grey[600],
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
              }}
            >
              <UndoIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
            <IconButton
              onClick={handleRedo}
              disabled={historyIndex >= historyLength - 1}
              aria-label="Redo"
              sx={{
                width: 24,
                height: 24,
                color: historyIndex < historyLength - 1 ? theme.palette.primary.main : theme.palette.grey[600],
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
              }}
            >
              <RedoIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
        </Box>
        <Box
          aria-live="polite"
          sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, flexWrap: 'wrap', justifyContent: isCompactTable ? 'flex-end' : 'flex-start' }}
        >
          <Typography
            variant="caption"
            data-testid="quick-fill-row-status"
            sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}
          >
            {visibleRowStatus}
          </Typography>
          {selectedVisibleRowCount > 0 && (
            <Typography variant="caption" sx={{ color: theme.palette.primary.light, fontSize: '0.68rem' }}>
              {selectedVisibleRowStatus}
            </Typography>
          )}
          {pendingChangeCount > 0 && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}>
              {pendingChangeCount} pending change{pendingChangeCount === 1 ? '' : 's'}
            </Typography>
          )}
          {hasActiveFilters && (
            <>
              <Typography variant="caption" sx={{ color: theme.palette.secondary.light, fontSize: '0.68rem' }}>
                {activeFilterCount} active filter{activeFilterCount === 1 ? '' : 's'}
              </Typography>
              <Button
                onClick={resetFilters}
                variant="text"
                size="small"
                aria-label="Clear all filters"
                sx={{
                  minWidth: 'auto',
                  px: 0.75,
                  fontSize: '0.68rem',
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                Clear filters
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(FilterableTable);
